import React, { FC, useState, useEffect, useCallback } from "react";

import { Tab } from "../../atoms/Tab";
import { HeaderText, TabTitleWrapper, Wrapper, NameWrapper, StarIcon } from "./elements";
import { data } from "../../../constants";
import { Table } from "../../organisms/Table";
import { NewApplicantList } from "../../../core/entities/applicant";
import { FilterFilled } from "@ant-design/icons";
import { star } from "../../../assets/icons/index";

type Props = {
  isLoading: boolean;
  readApplicants: NewApplicantList[];
  unreadApplicants: NewApplicantList[];
  onClickApplicant: (key: number) => void;
  onChange?: (subStatus:string, pagination: any, filters: any) => void;
  onTabClick?: (key: string, event: any) => void;
  unreadTotal: number;
  readTotal: number;
  currentReadPage?: number | undefined;
  currentUnreadPage?: number | undefined;
};

const NewApplicants: FC<Props> = ({
  isLoading,
  onClickApplicant,
  readApplicants,
  unreadApplicants,
  unreadTotal,
  readTotal,
  onChange,
  onTabClick,
  currentReadPage,
  currentUnreadPage,
}) => {
  const [filterDropdown, setFilterDropdownVisible] = useState<any>({
    isVisible: false,
    dropdownKey: "",
  });
  const [readFilterDropdown, setReadFilterDropdownVisible] = useState<any>({
    isVisible: false,
    dropdownKey: "",
  });

  const [rowRecord, setRowRecord] = useState<any>(null);

  useEffect(() => {
    if (rowRecord) {
      onClickApplicant(rowRecord.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowRecord]);

  const onScrollPage = useCallback(() => {
    setFilterDropdownVisible({ ...filterDropdown, isVisible: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("scroll", onScrollPage);
    return () => {
      document.removeEventListener("scroll", onScrollPage);
    };
  }, [onScrollPage]);

  const handleReadOnChange = (pagination: any, filters: any) => {
    onChange && onChange('read', pagination, filters);
  };

  const handleUnreadOnChange = (pagination: any, filters: any) => {
    onChange && onChange('unread', pagination, filters);
  };

  const newApplicantColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "280px",
      fixed: "left",
      render: (text: string, row: any): any => {
        return row.referredBy ? (
          <NameWrapper>
            <span>{text}</span>
            <StarIcon src={star} />
          </NameWrapper>
        ) : text;
      },
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      width: "220px",
      filterIcon: (
        <FilterFilled
          className={
            filterDropdown.dropdownKey === "position" &&
            filterDropdown.isVisible
              ? "tableFilterDropdownPositionActive"
              : ""
          }
        />
      ),
      filterDropdownVisible:
        filterDropdown.isVisible && filterDropdown.dropdownKey === "position",
      onFilterDropdownVisibleChange: (visible: any) => {
        setFilterDropdownVisible({
          isVisible: visible,
          dropdownKey: "position",
        });
      },
      filters: data.positionApplied.map((position: string) => {
        return { text: position, value: position };
      }),
      onFilter: (value: any, record: any) =>
        record.position.indexOf(value) === 0,
    },
    {
      title: "Date Applied",
      dataIndex: "dateApplied",
      key: "dateApplied",
      width: "200px",
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: "300px",
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      width: "180px",
    },
  ];

  const newApplicantColumnRead = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "280px",
      fixed: "left",
      render: (text: string, row: any): any => {
        return row.referredBy ? (
          <NameWrapper>
            <span>{text}</span>
            <StarIcon src={star} />
          </NameWrapper>
        ) : text;
      },
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      width: "220px",
      filterIcon: (
        <FilterFilled
          className={
            readFilterDropdown.dropdownKey === "position" &&
            readFilterDropdown.isVisible
              ? "tableFilterDropdownPositionActive"
              : ""
          }
        />
      ),
      filterDropdownVisible:
        readFilterDropdown.isVisible &&
        readFilterDropdown.dropdownKey === "position",
      onFilterDropdownVisibleChange: (visible: any) => {
        setReadFilterDropdownVisible({
          isVisible: visible,
          dropdownKey: "position",
        });
      },
      filters: data.positionApplied.map((position: string) => {
        return { text: position, value: position };
      }),
      onFilter: (value: any, record: any) => 
        record.position.indexOf(value) === 0,
    },
    {
      title: "Date Applied",
      dataIndex: "dateApplied",
      key: "dateApplied",
      width: "200px",
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: "300px",
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      width: "180px",
    },
  ];

  const tabs = [
    {
      //this tab only shows data with a status read of false
      title: (
        <TabTitleWrapper>
          <p>Unread </p>
          <p>({unreadTotal})</p>
        </TabTitleWrapper>
      ),
      content: (
        <Table
          isLoading={isLoading}
          tableData={unreadApplicants}
          columns={newApplicantColumns}
          rowRecord={setRowRecord}
          totalItems={unreadTotal}
          onChange={handleUnreadOnChange}
          current={currentUnreadPage}
        />
      ),
    },
    {
      //this tab only shows data with a status read of true
      title: (
        <TabTitleWrapper>
          <p>Read </p>
          <p>({readTotal})</p>
        </TabTitleWrapper>
      ),
      content: (
        <Table
          isLoading={isLoading}
          tableData={readApplicants}
          columns={newApplicantColumnRead}
          rowRecord={setRowRecord}
          totalItems={readTotal}
          onChange={handleReadOnChange}
          current={currentReadPage}
        />
      ),
    },
  ];

  return (
    <Wrapper onScroll={onScrollPage}>
      <HeaderText>New Applicants</HeaderText>
      <Tab tabs={tabs} onTabClick={onTabClick} />
    </Wrapper>
  );
};

export default NewApplicants;
